import React from 'react';
import { motion } from 'framer-motion';
import { GraduationCap, Users, Brain, Target, Shield, Book, Video, Award, CheckCircle, ArrowRight, Presentation, UserCheck, Clock } from 'lucide-react';

interface TrainingAwarenessProps {
  setCurrentPage: (page: string) => void;
}

const TrainingAwareness: React.FC<TrainingAwarenessProps> = ({ setCurrentPage }) => {
  const features = [
    {
      icon: Brain,
      title: "Interactive Learning",
      description: "Engaging training modules with hands-on exercises and real-world scenarios."
    },
    {
      icon: Target,
      title: "Targeted Programs",
      description: "Role-specific security training tailored to different organizational levels."
    },
    {
      icon: Shield,
      title: "Security Best Practices",
      description: "Comprehensive coverage of cybersecurity best practices and protocols."
    },
    {
      icon: Users,
      title: "Team Development",
      description: "Collaborative learning experiences to build a security-aware culture."
    }
  ];

  const programs = [
    {
      title: "Security Fundamentals",
      items: [
        "Basic security principles",
        "Password management",
        "Email security",
        "Social engineering awareness"
      ]
    },
    {
      title: "Advanced Security",
      items: [
        "Threat detection techniques",
        "Incident response procedures",
        "Data protection methods",
        "Security tool usage"
      ]
    },
    {
      title: "Compliance Training",
      items: [
        "Regulatory requirements",
        "Policy compliance",
        "Privacy guidelines",
        "Security protocols"
      ]
    }
  ];

  const methods = [
    {
      icon: Video,
      title: "Video Training",
      description: "High-quality video content covering essential security topics and demonstrations."
    },
    {
      icon: Presentation,
      title: "Live Workshops",
      description: "Interactive workshops led by security experts for hands-on learning."
    },
    {
      icon: Book,
      title: "Learning Resources",
      description: "Comprehensive documentation and guides for self-paced learning."
    }
  ];

  const benefits = [
    {
      icon: UserCheck,
      title: "Employee Empowerment",
      items: [
        "Enhanced security awareness",
        "Improved decision-making",
        "Increased confidence",
        "Better risk management"
      ]
    },
    {
      icon: Shield,
      title: "Organizational Security",
      items: [
        "Reduced security incidents",
        "Stronger security culture",
        "Improved compliance",
        "Risk mitigation"
      ]
    },
    {
      icon: Clock,
      title: "Continuous Learning",
      items: [
        "Regular updates",
        "Skill advancement",
        "Knowledge retention",
        "Performance tracking"
      ]
    }
  ];

  return (
    <div className="min-h-screen hero-gradient pt-32 pb-20">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        {/* Hero Section */}
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-20">
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              className="inline-block mb-6"
            >
              <GraduationCap className="h-16 w-16 text-primary animate-glow" />
            </motion.div>
            
            <motion.h1
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.1 }}
              className="text-4xl md:text-5xl font-bold mb-6"
            >
              <span className="text-gradient">
                Security Training & Awareness
              </span>
            </motion.h1>
            
            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.2 }}
              className="text-lg text-muted max-w-3xl mx-auto"
            >
              Comprehensive security training programs to build a strong security culture in your organization
            </motion.p>
          </div>
        </div>

        {/* Features Grid */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Training Features</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive security training and awareness features
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {features.map((feature, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.3 + index * 0.1 }}
                className="glass-panel rounded-xl p-6 hover-card"
              >
                <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg mb-6">
                  <feature.icon className="h-7 w-7 text-white" />
                </div>
                <h3 className="text-xl font-semibold mb-3">
                  {feature.title}
                </h3>
                <p className="text-muted">
                  {feature.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Training Programs */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Training Programs</span>
            </h2>
            <p className="text-lg text-muted">
              Comprehensive security training programs for all skill levels
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {programs.map((program, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.4 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <h3 className="text-2xl font-semibold mb-6">
                  {program.title}
                </h3>
                <ul className="space-y-4">
                  {program.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Training Methods */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Training Methods</span>
            </h2>
            <p className="text-lg text-muted">
              Multiple learning approaches to suit different needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {methods.map((method, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.5 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 text-center hover-card"
              >
                <div className="flex justify-center mb-6">
                  <div className="w-16 h-16 bg-gradient-to-br from-blue-500 to-purple-500 rounded-xl flex items-center justify-center">
                    <method.icon className="h-8 w-8 text-white" />
                  </div>
                </div>
                <h3 className="text-2xl font-semibold mb-4">
                  {method.title}
                </h3>
                <p className="text-muted">
                  {method.description}
                </p>
              </motion.div>
            ))}
          </div>
        </div>

        {/* Benefits Section */}
        <div className="mb-20">
          <div className="text-center mb-16">
            <h2 className="text-3xl md:text-4xl font-bold mb-6">
              <span className="text-gradient">Training Benefits</span>
            </h2>
            <p className="text-lg text-muted">
              Key advantages of our security training programs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {benefits.map((benefit, index) => (
              <motion.div
                key={index}
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.6 + index * 0.1 }}
                className="glass-panel rounded-xl p-8 hover-card"
              >
                <div className="flex items-center mb-6">
                  <div className="flex items-center justify-center w-14 h-14 bg-gradient-to-br from-blue-500 to-purple-500 rounded-lg">
                    <benefit.icon className="h-7 w-7 text-white" />
                  </div>
                  <h3 className="text-2xl font-semibold ml-4">
                    {benefit.title}
                  </h3>
                </div>
                <ul className="space-y-4">
                  {benefit.items.map((item, itemIndex) => (
                    <li key={itemIndex} className="flex items-center">
                      <CheckCircle className="h-5 w-5 text-primary mr-3" />
                      <span className="text-muted">{item}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>

        {/* CTA Section */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.7 }}
          className="text-center"
        >
          <div className="glass-panel rounded-2xl p-8 md:p-12">
            <div className="max-w-3xl mx-auto text-center">
              <h2 className="text-3xl font-bold mb-6">
                Enhance Your Security Knowledge
              </h2>
              <p className="text-lg text-muted mb-8 max-w-2xl mx-auto">
                Start your security training journey today
              </p>
              <button 
                onClick={() => setCurrentPage('get-started')}
                className="btn-primary"
              >
                <span className="flex items-center justify-center">
                  Request Consultation
                  <ArrowRight className="ml-2 h-5 w-5" />
                </span>
              </button>
            </div>
          </div>
        </motion.div>
      </div>
    </div>
  );
};

export default TrainingAwareness;